<template>
  <bulk-update-wrapper
    :saving="saving"
    title="Unapprove Jobs"
    :button-label="buttonLabel"
    :has-changes="true"
    @save="save"
    @dismiss="dismiss"
  >
    <vs-text>
      {{ selectedJobs.length }}
      {{ selectedJobs.length > 1 ? 'jobs' : 'job' }} will be unapproved and marked pending approval
    </vs-text>

    <template #error-snackbar>
      <vs-snackbar :value="error" type="error" sticky @input="clearSnackbar">
        Failed to unapprove jobs
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useBulkUnapproveMutation } from '@/api/jobs'
import BulkUpdateWrapper from '../common/BulkUpdateWrapper.vue'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { JobJson } from '@/models/types/job-json'

const emits = defineEmits<{
  (e: 'dismiss'): void
  (e: 'updated', response: BulkUpdateResponse): void
}>()

const props = defineProps<{
  selectedJobs: JobJson[]
}>()

const error = ref(false)
const saving = ref(false)

const { mutateAsync } = useBulkUnapproveMutation()

const buttonLabel = computed<string>(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2 ? `Unapprove ${jobCount} jobs` : 'Unapprove job'
})

const dismiss = () => emits('dismiss')
const clearSnackbar = () => (error.value = false)

const save = async () => {
  saving.value = true
  try {
    await mutateAsync({
      jobIds: props.selectedJobs.map((job) => job.id),
    })
    const updatedJobs: JobJson[] = props.selectedJobs.map(({ ...job }) =>
      Object.assign(job, { approval_status: 'PendingApproval', approved_by: null }),
    )
    emits('updated', { message: 'Jobs unapproved', updatedJobs })
  } catch {
    error.value = true
  } finally {
    saving.value = false
  }
}
</script>
